import { Preferences } from '@capacitor/preferences';
import type { StateStorage } from 'zustand/middleware';
import { isNative } from '@/lib/platform';

const nativeStorage: StateStorage = {
  getItem: async (name) => (await Preferences.get({ key: name })).value,
  setItem: async (name, value) => {
    await Preferences.set({ key: name, value });
  },
  removeItem: async (name) => {
    await Preferences.remove({ key: name });
  },
};

const webStorage: StateStorage = {
  getItem: (name) => globalThis.localStorage.getItem(name),
  setItem: (name, value) => {
    globalThis.localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    globalThis.localStorage.removeItem(name);
  },
};

export const stateStorage: StateStorage = isNative() ? nativeStorage : webStorage;

const DATE_TAG = '__t';
const DATE_KIND = 'date';

// JSON.stringify calls Date.prototype.toJSON before invoking the replacer,
// so `value` is already the ISO string by the time we see it. Read the raw
// value off the holder via `this[key]` to detect Date instances.
export function jsonReplacer(this: Record<string, unknown>, key: string, value: unknown): unknown {
  const raw = this[key];
  if (raw instanceof Date) {
    // Universal backstop: an Invalid Date makes toISOString() throw RangeError
    // mid-serialize, which aborts the ENTIRE store's persist write and bubbles
    // uncaught into the app error boundary (looks to the user like a crash /
    // being signed out). Degrade a bad Date to null so one malformed value can
    // never brick persistence. Callers should still guard their inputs so a bad
    // value never reaches the store in the first place.
    return Number.isNaN(raw.getTime()) ? null : { [DATE_TAG]: DATE_KIND, v: raw.toISOString() };
  }
  return value;
}

export const jsonReviver = (_key: string, value: unknown): unknown => {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as Record<string, unknown>)[DATE_TAG] === DATE_KIND &&
    typeof (value as Record<string, unknown>).v === 'string'
  ) {
    return new Date((value as { v: string }).v);
  }
  return value;
};
